// Import UserRole from users constants
import { UserRole } from '@/feature/users/constants';
import { useGetCurrentUserProfile } from '@/feature/user-settings/hooks/userSettingApi';

// Permission definitions
export enum Permission {
  VIEW_PROFILES = 'VIEW_PROFILES',
  EDIT_PROFILES = 'EDIT_PROFILES',
  CREATE_PROFILES = 'CREATE_PROFILES',
  VIEW_USERS = 'VIEW_USERS',
  EDIT_USERS = 'EDIT_USERS',
  INVITE_USERS = 'INVITE_USERS',
  DEACTIVATE_USERS = 'DEACTIVATE_USERS',
  REINVITE_USERS = 'REINVITE_USERS',
  VIEW_PATIENTS = 'VIEW_PATIENTS',
  ADD_PATIENTS = 'ADD_PATIENTS',
  EDIT_PATIENTS = 'EDIT_PATIENTS',
  MOVE_PATIENTS = 'MOVE_PATIENTS',
  REQUEST_WORKFLOW = 'REQUEST_WORKFLOW',
  UPLOAD_DOCUMENTS = 'UPLOAD_DOCUMENTS',
  VIEW_DOWNLOADS = 'VIEW_DOWNLOADS',
  GENERATE_DOWNLOADS = 'GENERATE_DOWNLOADS', // Reserved; currently unused by the frontend.
  DOWNLOAD_DOWNLOADS = 'DOWNLOAD_DOWNLOADS',
  EXPORT_MEDICAL_RECORDS = 'EXPORT_MEDICAL_RECORDS',
  VIEW_CLINICAL_SUMMARY = 'VIEW_CLINICAL_SUMMARY', // Reserved; currently unused by the frontend.
  VIEW_USER_SETTINGS_SELF = 'VIEW_USER_SETTINGS_SELF',
  EDIT_USER_SETTINGS_SELF = 'EDIT_USER_SETTINGS_SELF',
  VIEW_ORGANIZATION_SETTINGS = 'VIEW_ORGANIZATION_SETTINGS',
  SUBMIT_DATA_DELETION = 'SUBMIT_DATA_DELETION',
  EXPORT_PATIENT_AUDIT_LOGS = 'EXPORT_PATIENT_AUDIT_LOGS',
  VIEW_REPORTING = 'VIEW_REPORTING',
  VIEW_EFAX = 'VIEW_EFAX',
}

// Role to permissions mapping
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  [UserRole.OWNER]: [
    Permission.VIEW_PROFILES,
    Permission.EDIT_PROFILES,
    Permission.CREATE_PROFILES,
    Permission.VIEW_USERS,
    Permission.EDIT_USERS,
    Permission.INVITE_USERS,
    Permission.DEACTIVATE_USERS,
    Permission.REINVITE_USERS,
    Permission.VIEW_PATIENTS,
    Permission.ADD_PATIENTS,
    Permission.EDIT_PATIENTS,
    Permission.MOVE_PATIENTS,
    Permission.REQUEST_WORKFLOW,
    Permission.UPLOAD_DOCUMENTS,
    Permission.VIEW_DOWNLOADS,
    Permission.GENERATE_DOWNLOADS,
    Permission.DOWNLOAD_DOWNLOADS,
    Permission.EXPORT_MEDICAL_RECORDS,
    Permission.VIEW_CLINICAL_SUMMARY,
    Permission.VIEW_USER_SETTINGS_SELF,
    Permission.EDIT_USER_SETTINGS_SELF,
    Permission.VIEW_ORGANIZATION_SETTINGS,
    Permission.SUBMIT_DATA_DELETION,
    Permission.EXPORT_PATIENT_AUDIT_LOGS,
    Permission.VIEW_REPORTING,
    Permission.VIEW_EFAX,
  ],
  [UserRole.MANAGER]: [
    Permission.VIEW_PROFILES,
    Permission.EDIT_PROFILES,
    Permission.VIEW_USERS,
    Permission.EDIT_USERS,
    Permission.INVITE_USERS,
    Permission.DEACTIVATE_USERS,
    Permission.REINVITE_USERS,
    Permission.VIEW_PATIENTS,
    Permission.ADD_PATIENTS,
    Permission.EDIT_PATIENTS,
    Permission.MOVE_PATIENTS,
    Permission.REQUEST_WORKFLOW,
    Permission.UPLOAD_DOCUMENTS,
    Permission.VIEW_DOWNLOADS,
    Permission.GENERATE_DOWNLOADS,
    Permission.DOWNLOAD_DOWNLOADS,
    Permission.EXPORT_MEDICAL_RECORDS,
    Permission.VIEW_CLINICAL_SUMMARY,
    Permission.VIEW_USER_SETTINGS_SELF,
    Permission.EDIT_USER_SETTINGS_SELF,
    Permission.EXPORT_PATIENT_AUDIT_LOGS,
    Permission.VIEW_REPORTING,
  ],
  [UserRole.EMPLOYEE]: [
    Permission.VIEW_PROFILES,
    Permission.VIEW_PATIENTS,
    Permission.ADD_PATIENTS,
    Permission.EDIT_PATIENTS,
    Permission.REQUEST_WORKFLOW,
    Permission.UPLOAD_DOCUMENTS,
    Permission.VIEW_DOWNLOADS,
    Permission.GENERATE_DOWNLOADS,
    Permission.DOWNLOAD_DOWNLOADS,
    Permission.EXPORT_MEDICAL_RECORDS,
    Permission.VIEW_CLINICAL_SUMMARY,
    Permission.VIEW_USER_SETTINGS_SELF,
    Permission.EDIT_USER_SETTINGS_SELF,
    Permission.VIEW_REPORTING,
  ],
  [UserRole.EMPLOYEE_EXPORT_LOCKED]: [
    Permission.VIEW_PROFILES,
    Permission.VIEW_PATIENTS,
    Permission.ADD_PATIENTS,
    Permission.EDIT_PATIENTS,
    Permission.REQUEST_WORKFLOW,
    Permission.UPLOAD_DOCUMENTS,
    Permission.VIEW_USER_SETTINGS_SELF,
    Permission.EDIT_USER_SETTINGS_SELF,
  ],
};

type UseHasPermissionOptions = {
  enabled?: boolean;
};

export const useHasPermission = (
  permissions: Permission | Permission[],
  requireAll: boolean = true,
  options: UseHasPermissionOptions = {},
) => {
  const permissionsArray = Array.isArray(permissions)
    ? permissions
    : [permissions];
  const hasPermissionsToCheck = permissionsArray.length > 0;
  const shouldFetchUser = (options.enabled ?? true) && hasPermissionsToCheck;
  const { data: currentUser, isLoading } = useGetCurrentUserProfile({
    enabled: shouldFetchUser,
  });

  if (!hasPermissionsToCheck) {
    return {
      hasPermission: true,
      currentUser,
      isLoading: false,
    };
  }

  if (!shouldFetchUser) {
    return {
      hasPermission: false,
      currentUser: undefined,
      isLoading: false,
    };
  }

  if (!currentUser) {
    return {
      hasPermission: false,
      currentUser,
      isLoading,
    };
  }

  const checkPermission = (permission: Permission): boolean => {
    return ROLE_PERMISSIONS[currentUser.role]?.includes(permission) ?? false;
  };

  const hasRequiredPermissions = requireAll
    ? permissionsArray.every(checkPermission)
    : permissionsArray.some(checkPermission);

  return {
    hasPermission: hasRequiredPermissions,
    currentUser,
    isLoading,
  };
};
